"use client";

import type { ExternalClientApp } from "@mlx/contracts";
import {
  BlocksIcon,
  BracesIcon,
  MonitorIcon,
  TerminalIcon,
} from "lucide-react";
import { useState } from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  clientIcon,
  sourceIcon,
} from "@/components/content/icons/source-icons";
import { cn } from "@/lib/utils";
import { TwinLiveMark } from "@/components/app/twin/twin-live-mark";

export type ProvenanceActor = {
  label: string;
  imageUrl: string | null;
  initials: string;
  icon?: "twin";
  identityId?: string;
};
export type ProvenanceInterface =
  | "web"
  | "cli"
  | "api"
  | "mcp"
  | "ios"
  | "slack"
  | "teams"
  | "copilot";

const PROVENANCE_INTERFACE_TYPES: ReadonlySet<string> = new Set([
  "web",
  "cli",
  "api",
  "mcp",
  "ios",
  "slack",
  "teams",
  "copilot",
] satisfies ProvenanceInterface[]);

/** Narrow an untyped source value to a known provenance interface — the one
 *  allow-list every surface shares, so new surfaces light up everywhere. */
export function provenanceInterfaceType(
  value: unknown,
): ProvenanceInterface | undefined {
  return typeof value === "string" && PROVENANCE_INTERFACE_TYPES.has(value)
    ? (value as ProvenanceInterface)
    : undefined;
}

export function ProvenanceAvatarStack(props: {
  actor?: ProvenanceActor | null;
  clientApp?: ExternalClientApp | null;
  interfaceType?: ProvenanceInterface | null;
  usedTwin?: boolean;
  executedByTwin?: boolean;
  action: "started" | "created" | "updated" | "posted" | "retrieved";
  size?: "sm" | "md";
  layout?: "stack" | "overlay";
  /** The Twin is still working on this record: its mark breathes. */
  working?: boolean;
  /** Breathe for this long after mount, or again when `restartKey` changes. */
  workingSeconds?: number;
  restartKey?: string | number;
  className?: string;
}) {
  const sourceVisible = Boolean(props.clientApp || props.interfaceType);
  // When the actor IS the twin its disc already carries the identity —
  // badging a second twin mark onto it double-marks and mangles the avatar.
  const twinVisible =
    Boolean(props.usedTwin || props.executedByTwin) &&
    props.actor?.icon !== "twin";
  if (!props.actor && !sourceVisible && !twinVisible) return null;
  const description = provenanceDescription(props);
  const sizeClass = props.size === "md" ? "size-8" : "size-5";
  const iconClass = props.size === "md" ? "size-4" : "size-3";
  const overlay =
    props.layout === "overlay" &&
    Boolean(props.actor) &&
    (sourceVisible || twinVisible);
  const actorMark = props.actor ? (
    <ActorMark
      actor={props.actor}
      className={sizeClass}
      working={props.working}
      workingSeconds={props.workingSeconds}
      restartKey={props.restartKey}
    />
  ) : null;
  return (
    <TooltipProvider delay={180}>
      <Tooltip>
        <TooltipTrigger
          render={
            <span
              role="img"
              aria-label={description}
              className={cn(
                "inline-flex items-center",
                overlay ? "relative" : "-space-x-1.5",
                props.className,
              )}
              tabIndex={0}
            />
          }
        >
          {overlay ? actorMark : null}
          {overlay ? (
            <span
              data-slot="provenance-badges"
              className="absolute right-0 -top-1 flex flex-col -space-y-1"
              aria-hidden
            >
              {twinVisible ? (
                <TwinProvenanceMark
                  className="size-4 ring-1"
                  working={props.working}
                  workingSeconds={props.workingSeconds}
                  restartKey={props.restartKey}
                />
              ) : null}
              {sourceVisible ? (
                <SourceMark
                  clientApp={props.clientApp}
                  interfaceType={props.interfaceType}
                  className="size-4 ring-1"
                  iconClassName="size-2.5"
                />
              ) : null}
            </span>
          ) : (
            <>
              {actorMark}
              {sourceVisible ? (
                <SourceMark
                  clientApp={props.clientApp}
                  interfaceType={props.interfaceType}
                  className={sizeClass}
                  iconClassName={iconClass}
                />
              ) : null}
              {twinVisible ? (
                // The Twin mark nestles into the stack as itself, the way the
                // thinking line shows it, rather than plated inside a disc.
                <TwinLiveMark
                  active={props.working}
                  className={cn(sizeClass, "relative")}
                  durationSeconds={props.workingSeconds}
                  restartKey={props.restartKey}
                />
              ) : null}
            </>
          )}
        </TooltipTrigger>
        <TooltipContent sideOffset={6}>{description}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

const markClass =
  "relative flex shrink-0 items-center justify-center rounded-full border border-border/70 bg-background ring-2 ring-background";

function SourceMark(props: {
  clientApp?: ExternalClientApp | null;
  interfaceType?: ProvenanceInterface | null;
  className: string;
  iconClassName: string;
}) {
  return (
    <span
      className={cn(
        markClass,
        "overflow-hidden text-foreground",
        props.className,
      )}
      aria-hidden
    >
      <SourceIcon
        clientApp={props.clientApp}
        interfaceType={props.interfaceType}
        className={sourceIconClassName(props.clientApp, props.iconClassName)}
      />
    </span>
  );
}

type TwinWorking = {
  working?: boolean;
  workingSeconds?: number;
  restartKey?: string | number;
};

function TwinProvenanceMark(props: TwinWorking & { className: string }) {
  // The disc IS the avatar: full-bleed rings fill the circle instead of
  // floating on the bg-background plate the other marks need. While the Twin
  // is working the same disc breathes instead of standing still.
  return (
    <span
      className={cn(markClass, "overflow-hidden", props.className)}
      aria-hidden
    >
      <TwinLiveMark
        bleed
        active={props.working}
        className="size-full"
        durationSeconds={props.workingSeconds}
        restartKey={props.restartKey}
      />
    </span>
  );
}

export function ActorMark({
  actor,
  className,
  working = false,
  workingSeconds,
  restartKey,
}: TwinWorking & {
  actor: ProvenanceActor;
  className: string;
}) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const showImage = Boolean(actor.imageUrl && actor.imageUrl !== failedUrl);
  return (
    <span
      className={cn(
        markClass,
        "overflow-hidden bg-muted text-3xs font-semibold text-muted-foreground",
        className,
      )}
      aria-hidden
    >
      {actor.icon === "twin" ? (
        <TwinLiveMark
          bleed
          active={working}
          className="size-full"
          durationSeconds={workingSeconds}
          restartKey={restartKey}
        />
      ) : showImage ? (
        <img
          src={actor.imageUrl ?? undefined}
          alt=""
          className="size-full object-cover"
          referrerPolicy="no-referrer"
          onError={() => setFailedUrl(actor.imageUrl)}
        />
      ) : (
        actor.initials
      )}
    </span>
  );
}

function sourceIconClassName(
  clientApp: ExternalClientApp | null | undefined,
  className: string,
) {
  const monochromeBrand =
    clientApp === "claude-code" ||
    clientApp === "codex" ||
    clientApp === "gemini";
  return cn(className, monochromeBrand && "dark:invert");
}

function SourceIcon(props: {
  clientApp?: ExternalClientApp | null;
  interfaceType?: ProvenanceInterface | null;
  className: string;
}) {
  const client = props.clientApp ? clientIcon(props.clientApp) : null;
  if (client) {
    return <img alt="" src={client.src} className={props.className} />;
  }
  // Messaging surfaces carry their brand marks like client apps do; the
  // one-colour Copilot mark inverts on dark like the monochrome brands.
  const surface =
    props.interfaceType === "slack" ||
    props.interfaceType === "teams" ||
    props.interfaceType === "copilot"
      ? sourceIcon(props.interfaceType)
      : null;
  if (surface) {
    return (
      <img
        alt=""
        src={surface.src}
        className={cn(props.className, surface.monochrome && "dark:invert")}
      />
    );
  }
  if (props.interfaceType === "cli")
    return <TerminalIcon className={props.className} />;
  if (props.interfaceType === "mcp")
    return <BlocksIcon className={props.className} />;
  if (props.interfaceType === "api")
    return <BracesIcon className={props.className} />;
  return <MonitorIcon className={props.className} />;
}

function provenanceDescription(props: {
  actor?: ProvenanceActor | null;
  clientApp?: ExternalClientApp | null;
  interfaceType?: ProvenanceInterface | null;
  usedTwin?: boolean;
  executedByTwin?: boolean;
  action: string;
}) {
  const actor = props.actor
    ? props.actor.identityId
      ? `${props.actor.label} (${props.actor.identityId})`
      : props.actor.label
    : "MLX";
  const app = clientAppLabel(props.clientApp);
  const via = props.interfaceType ? interfaceLabel(props.interfaceType) : null;
  const source = app
    ? ` using ${app}${via ? ` via ${via}` : ""}`
    : via
      ? ` via ${via}`
      : "";
  const twin = props.executedByTwin
    ? ". The Twin executed the action"
    : props.usedTwin
      ? " with governed data from the Twin"
      : "";
  return `${actor} ${props.action} this${source}${twin}.`;
}

export function clientAppLabel(value?: ExternalClientApp | null) {
  if (value === "claude-code") return "Claude Code";
  if (value === "codex") return "Codex";
  if (value === "gemini") return "Gemini";
  if (value === "chatgpt") return "ChatGPT";
  return null;
}

export function interfaceLabel(value: ProvenanceInterface) {
  if (value === "cli") return "CLI";
  if (value === "mcp") return "MCP";
  if (value === "api") return "API";
  if (value === "ios") return "iOS";
  if (value === "slack") return "Slack";
  if (value === "teams") return "Microsoft Teams";
  if (value === "copilot") return "Copilot";
  return "web app";
}

export function sessionClientProvenance(
  metadata: Record<string, unknown> | undefined,
): { clientApp?: ExternalClientApp; interfaceType?: ProvenanceInterface } {
  // External-conversation sessions (Slack, Teams, Copilot) record their
  // origin as a top-level metadata.source rather than a client object.
  const externalSource = metadata?.source;
  const external =
    externalSource === "slack" ||
    externalSource === "teams" ||
    externalSource === "copilot"
      ? { interfaceType: externalSource as ProvenanceInterface }
      : {};
  const client = metadata?.client;
  if (!client || typeof client !== "object" || Array.isArray(client))
    return external;
  const record = client as Record<string, unknown>;
  const app = record.app;
  const source = record.source;
  return {
    ...external,
    ...(app === "claude-code" ||
    app === "codex" ||
    app === "gemini" ||
    app === "chatgpt"
      ? { clientApp: app as ExternalClientApp }
      : {}),
    ...(source === "web" ||
    source === "cli" ||
    source === "api" ||
    source === "mcp" ||
    source === "ios"
      ? { interfaceType: source as ProvenanceInterface }
      : {}),
  };
}

export function channelClientProvenance(
  metadata: Record<string, unknown> | undefined,
) {
  const value = metadata?.mlxProvenance;
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const record = value as Record<string, unknown>;
  const app = record.clientApp;
  const source = record.interfaceType;
  return {
    ...(app === "claude-code" ||
    app === "codex" ||
    app === "gemini" ||
    app === "chatgpt"
      ? { clientApp: app as ExternalClientApp }
      : {}),
    ...(source === "web" ? { interfaceType: "web" as const } : {}),
  };
}
